import React from 'react';
import { Button, DatePicker, Form, Input, Select } from 'antd';
import { isAccountActive, openNotification } from '../../../utils';
import dayjs from 'dayjs';
import { APP_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { listMeasuringInstrumentInventorysAll } from '../../measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import { listInspectorsAll } from '../../inspectors/services/inspectorService';
import { createInspectionTask, updateInspectionTask, getInspectionTask } from '../services/inspection-taskService';
import { CreateInspectionTaskDto } from '../types/InspectionTask';

export default function InspectionTaskForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateInspectionTaskDto>();

  const [measuringInstrumentInventoryOptions, setMeasuringInstrumentInventoryOptions] = React.useState<any[]>([]);
  const [attributeOptions, setAttributeOptions] = React.useState<any[]>([]);
  const [accountOptions, setAccountOptions] = React.useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = React.useState<number | undefined>(undefined);
  const [selectedInspectorOption, setSelectedInspectorOption] = React.useState<any | null>(null);

  const resolveInspectorAccountId = React.useCallback((inspector: any): number | undefined => {
    const accountId = Number(inspector?.account?.id ?? inspector?.id);
    return Number.isNaN(accountId) || accountId <= 0 ? undefined : accountId;
  }, []);

  const effectiveAccountOptions = React.useMemo(() => {
    const activeOptions = (accountOptions || []).filter((inspector) => isAccountActive(inspector));
    if (!id || !selectedAccountId) {
      return activeOptions;
    }
    const exists = activeOptions.some(
      (inspector) => resolveInspectorAccountId(inspector) === selectedAccountId,
    );
    if (exists) {
      return activeOptions;
    }
    const fallback =
      (selectedInspectorOption && resolveInspectorAccountId(selectedInspectorOption) === selectedAccountId
        ? selectedInspectorOption
        : null) ||
      {
        id: selectedAccountId,
        account: {
          id: selectedAccountId,
          firstName: 'Uzman',
          lastName: `#${selectedAccountId}`,
        },
      };
    return [...activeOptions, fallback];
  }, [accountOptions, id, resolveInspectorAccountId, selectedAccountId, selectedInspectorOption]);

  React.useEffect(() => {
    if (id) {
      getInspectionTask(id).then((d) => {
        const data: any = { ...d };
        const accountSource =
          (d as any)?.account && typeof (d as any).account === 'object'
            ? (d as any).account
            : (d as any)?.inspector?.account && typeof (d as any).inspector?.account === 'object'
              ? (d as any).inspector.account
              : undefined;
        if (data.date) data.date = dayjs(data.date);
        if (data.measuringInstrumentInventory)
          data.measuringInstrumentInventory =
            typeof data.measuringInstrumentInventory === 'object'
              ? data.measuringInstrumentInventory.id
              : data.measuringInstrumentInventory;
        if (data.attribute)
          data.attribute =
            typeof data.attribute === 'object' ? data.attribute.id : data.attribute;
        if (data.account)
          data.account =
            typeof data.account === 'object' ? data.account.id : data.account;
        else if (data.inspector)
          data.account =
            typeof data.inspector === 'object' ? data.inspector.account?.id : data.inspector;
        const normalizedSelectedAccountId = Number(data.account);
        setSelectedAccountId(
          Number.isNaN(normalizedSelectedAccountId) || normalizedSelectedAccountId <= 0
            ? undefined
            : normalizedSelectedAccountId,
        );
        setSelectedInspectorOption(
          accountSource
            ? {
              id: accountSource?.id,
              account: accountSource,
            }
            : null,
        );
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      setSelectedAccountId(undefined);
      setSelectedInspectorOption(null);
    }
    listMeasuringInstrumentInventorysAll().then(setMeasuringInstrumentInventoryOptions);
    apiFetch('/attributes').then(setAttributeOptions);
    listInspectorsAll().then(setAccountOptions);
  }, [id, form]);
  const onFinish = async (values: CreateInspectionTaskDto) => {
    const data: any = { ...values }; if (data.date) data.date = data.date.toISOString(); if (referenceId !== undefined) data.appointment = referenceId;
    try {
      if (id) {
        await updateInspectionTask(id, data);
        openNotification('success', 'Başarılı', 'Denetim Görevi Güncellendi');
      } else {
        await createInspectionTask(data);
        openNotification('success', 'Başarılı', 'Denetim Görevi Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Denetim Görevi işlemi başarısız');
    }
  };
  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical'>
      <Form.Item name='measuringInstrumentInventory' label='Cihaz (Envanter)' rules={[{ required: true }]}>
        <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}>
          {measuringInstrumentInventoryOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.serialNumber ?? o.id}</Select.Option>)}
        </Select>
      </Form.Item>
      <Form.Item name='attribute' label='Parametre' rules={[{ required: true }]}>
        <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}>
          {attributeOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>)}
        </Select>
      </Form.Item>
      <Form.Item name='account' label='P.K.M Uzmanı' rules={[{ required: true }]}> 
        <Select
          showSearch
          filterOption={(input, option) =>
            ((option?.children?.toString()) || '')
              .toLocaleLowerCase('tr')
              .includes(input.toLocaleLowerCase('tr'))
          }
          placeholder='Seçiniz'
          style={{ width: '100%' }}
        >
          {effectiveAccountOptions.map((o) => {
            const accountId = o?.account?.id ?? o?.id;
            const email = o?.account?.email;
            const fullName = `${o?.account?.firstName ?? o?.firstName ?? ''} ${o?.account?.lastName ?? o?.lastName ?? ''}`.trim();
            const label = email || (fullName.length ? fullName : accountId);
            return (
              <Select.Option key={accountId} value={accountId}>
                {label}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.Item name='date' label='Tarih/Saat' rules={[{ required: true }]}>
        <DatePicker showTime={{ format: 'HH:mm', minuteStep: 5 }} format={APP_DATE_FORMAT} placeholder='Seçiniz' style={{width:'100%'}} />
      </Form.Item>
      <Form.Item name='status' label='Durum' rules={[{ required: true }]}>
        <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}><Select.Option value='PLANNED'>Planlandı</Select.Option><Select.Option value='IN_PROGRESS'>Devam Ediyor</Select.Option><Select.Option value='COMPLETED'>Tamamlandı</Select.Option><Select.Option value='CANCELED'>İptal Edildi</Select.Option><Select.Option value='POSTPONED'>Ertelendi</Select.Option><Select.Option value='NO_SHOW'>Katılmadı / Gelmedi</Select.Option></Select>
      </Form.Item>
      <Form.Item name='agreementNo' label='e-Devlet No'>
        <Input style={{width:'100%'}} />
      </Form.Item>
      <Button htmlType='submit' type='primary' block>Kaydet</Button>
    </Form>
    </>
  );
}
